"use client";

import dynamic from "next/dynamic";
import { useState, useRef, useCallback, useEffect } from "react";

// ── Lazy-load Three.js (SSR must be disabled for WebGL) ───────────────────
const ThreeViewer = dynamic(() => import("@/components/ThreeViewer"), {
  ssr: false,
  loading: () => (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#08080f",
        color: "rgba(167,139,250,0.5)",
        fontSize: "0.875rem",
        letterSpacing: "0.05em",
      }}
    >
      Loading 3D engine…
    </div>
  ),
});

// ── Types ──────────────────────────────────────────────────────────────────

type GenerationMode = "image" | "prompt";
type JobStatus = "idle" | "processing" | "done" | "error";

interface Job {
  task_id: string;
  status: JobStatus;
  modelUrl?: string;
  error?: string;
}

interface UserInfo {
  name: string | null;
  email: string | null;
  credits: number;
}

// ── Studio page ────────────────────────────────────────────────────────────

export default function StudioPage() {
  const [mode, setMode] = useState<GenerationMode>("prompt");
  const [prompt, setPrompt] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [authError, setAuthError] = useState(false);
  const [stlLoading, setStlLoading] = useState(false);
  const [stlInfo, setStlInfo] = useState<{ watertight: boolean; faces: number } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Fetch user session and credits ──────────────────────────────────────
  useEffect(() => {
    fetch("/api/user/me")
      .then((r) => {
        if (r.status === 401) { setAuthError(true); return null; }
        return r.json();
      })
      .then((data) => { if (data) setUser(data); })
      .catch(() => {});
  }, []);

  // ── Poll task until done ─────────────────────────────────────────────────
  const startPolling = useCallback((taskId: string) => {
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 60) {
        clearInterval(pollRef.current!);
        setJob((j) => j ? { ...j, status: "error", error: "Timeout after 5 min" } : j);
        setIsGenerating(false);
        return;
      }

      try {
        const res = await fetch(`/api/generate?task_id=${taskId}`);
        const data = await res.json();

        // Hi3D statuses: queued → processing → succeeded / failed
        if (data.status === "succeeded" || data.status === "done") {
          clearInterval(pollRef.current!);
          setJob({
            task_id: taskId,
            status: "done",
            modelUrl: data.model_urls?.glb ?? data.glb_url ?? data.output_url,
          });
          setIsGenerating(false);
        } else if (data.status === "failed" || data.status === "error") {
          clearInterval(pollRef.current!);
          setJob({ task_id: taskId, status: "error", error: data.message ?? "Generation failed" });
          setIsGenerating(false);
        } else {
          setJob((j) => j ? { ...j, status: "processing" } : j);
        }
      } catch {
        // network hiccup — keep polling
      }
    }, 5000);
  }, []);

  // ── Handle generate ──────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (isGenerating) return;

    // Redirect to login if not authenticated
    if (authError) {
      window.location.href = "/login";
      return;
    }

    // Check credits locally before even hitting the API
    if (user && user.credits < 1) {
      setJob({ task_id: "", status: "error", error: "Sem créditos. Adquira mais para continuar gerando." });
      return;
    }

    setIsGenerating(true);
    setJob(null);

    try {
      const body: Record<string, string> = { mode };
      if (mode === "prompt") {
        if (!prompt.trim()) { setIsGenerating(false); return; }
        body.prompt = prompt.trim();
      } else {
        if (!imagePreview) { setIsGenerating(false); return; }
        body.imageUrl = imagePreview;
      }

      const res = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (res.status === 401) {
        setAuthError(true);
        setJob({ task_id: "", status: "error", error: "Sessão expirada. Faça login novamente." });
        setIsGenerating(false);
        return;
      }

      if (res.status === 402) {
        setJob({ task_id: "", status: "error", error: "Créditos insuficientes. Adquira mais para continuar." });
        setIsGenerating(false);
        return;
      }

      if (!res.ok || !data.task_id) {
        setJob({ task_id: "", status: "error", error: data.error ?? "Unknown error" });
        setIsGenerating(false);
        return;
      }

      setJob({ task_id: data.task_id, status: "processing" });
      startPolling(data.task_id);
    } catch (err) {
      setJob({ task_id: "", status: "error", error: String(err) });
      setIsGenerating(false);
    }
  };

  // ── Handle STL download ───────────────────────────────────────────────────
  const handleDownloadStl = async () => {
    if (!job?.modelUrl || stlLoading) return;
    setStlLoading(true);
    setStlInfo(null);

    try {
      const res = await fetch("/api/v1/export", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ glb_url: job.modelUrl, repair: true }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        alert(`Erro na conversão: ${err.error ?? res.statusText}`);
        setStlLoading(false);
        return;
      }

      // Captura metadados de qualidade da malha
      const watertight = res.headers.get("X-Mesh-Watertight") === "true";
      const faces = parseInt(res.headers.get("X-Mesh-Faces") ?? "0", 10);
      setStlInfo({ watertight, faces });

      // Dispara download automático no browser
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `3dmaker_${Date.now()}.stl`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Erro: ${String(err)}`);
    } finally {
      setStlLoading(false);
    }
  };

  // ── Handle image upload ───────────────────────────────────────────────────

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith("image/")) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  // ── Status display ───────────────────────────────────────────────────────
  const statusLabel = () => {
    if (!job) return null;
    if (job.status === "processing") return "⏳ Gerando modelo 3D… (pode levar 30–60s)";
    if (job.status === "done") return "✅ Modelo gerado com sucesso!";
    if (job.status === "error") return `❌ ${job.error}`;
    return null;
  };

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        width: "100vw",
        background: "#08080f",
        color: "#f0f0ff",
        fontFamily: "var(--font-inter, system-ui, sans-serif)",
        overflow: "hidden",
      }}
    >
      {/* ── Left Panel ──────────────────────────────────────────────── */}
      <aside
        style={{
          width: 340,
          minWidth: 300,
          height: "100%",
          background: "rgba(16,16,32,0.98)",
          borderRight: "1px solid rgba(255,255,255,0.07)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "1.25rem 1.5rem",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "linear-gradient(135deg, #7c3aed, #3b82f6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 16,
            }}
          >
            ◈
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: "0.95rem", letterSpacing: "-0.01em" }}>
              3D Studio
            </div>
            <div style={{ fontSize: "0.7rem", color: "rgba(160,160,200,0.6)" }}>
              Powered by N1vo AI
            </div>
          </div>

          {/* Credits badge + login */}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {user ? (
              <div
                title={`${user.credits} créditos restantes`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.3rem",
                  background: user.credits > 0 ? "rgba(52,211,153,0.12)" : "rgba(239,68,68,0.12)",
                  border: `1px solid ${user.credits > 0 ? "rgba(52,211,153,0.25)" : "rgba(239,68,68,0.25)"}`,
                  borderRadius: 8,
                  padding: "0.25rem 0.625rem",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: user.credits > 0 ? "#6ee7b7" : "#fca5a5",
                  cursor: "default",
                }}
                id="studio-credits-badge"
              >
                ✦ {user.credits} créditos
              </div>
            ) : authError ? (
              <a
                href="/login"
                style={{
                  fontSize: "0.7rem",
                  color: "#a78bfa",
                  textDecoration: "none",
                  background: "rgba(124,58,237,0.1)",
                  border: "1px solid rgba(124,58,237,0.2)",
                  borderRadius: 8,
                  padding: "0.25rem 0.625rem",
                }}
              >
                → Login
              </a>
            ) : (
              <div style={{ width: 70, height: 24, background: "rgba(255,255,255,0.04)", borderRadius: 8 }} />
            )}
            <a
              href="/"
              style={{ color: "rgba(160,160,200,0.5)", fontSize: "0.75rem", textDecoration: "none" }}
            >
              ← Home
            </a>
          </div>
        </div>

        {/* Mode toggle */}
        <div style={{ padding: "1.25rem 1.5rem 0" }}>
          <div style={{ fontSize: "0.7rem", color: "rgba(160,160,200,0.5)", marginBottom: "0.5rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>
            Modo de geração
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 4,
              background: "rgba(255,255,255,0.04)",
              borderRadius: 10,
              padding: 4,
            }}
          >
            {(["prompt", "image"] as const).map((m) => (
              <button
                key={m}
                id={`mode-${m}`}
                onClick={() => setMode(m)}
                style={{
                  padding: "0.5rem",
                  borderRadius: 7,
                  border: "none",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  transition: "all 0.2s",
                  background: mode === m ? "linear-gradient(135deg, #7c3aed, #3b82f6)" : "transparent",
                  color: mode === m ? "white" : "rgba(160,160,200,0.7)",
                }}
              >
                {m === "prompt" ? "✦ Texto" : "🖼 Imagem"}
              </button>
            ))}
          </div>
        </div>

        {/* Input area */}
        <div style={{ padding: "1.25rem 1.5rem", flex: 1, overflowY: "auto" }}>
          {mode === "prompt" ? (
            <div>
              <label style={{ fontSize: "0.7rem", color: "rgba(160,160,200,0.5)", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: "0.5rem" }}>
                Descreva o objeto 3D
              </label>
              <textarea
                id="studio-prompt-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ex: a modern ergonomic chair, smooth surfaces, minimal design..."
                rows={5}
                style={{
                  width: "100%",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 10,
                  padding: "0.875rem",
                  color: "#f0f0ff",
                  fontSize: "0.875rem",
                  resize: "vertical",
                  outline: "none",
                  fontFamily: "inherit",
                  lineHeight: 1.6,
                }}
              />

              {/* Quick prompts */}
              <div style={{ marginTop: "0.75rem" }}>
                <div style={{ fontSize: "0.65rem", color: "rgba(160,160,200,0.4)", marginBottom: "0.5rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                  Exemplos rápidos
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {[
                    "Modern coffee mug",
                    "Miniature castle",
                    "Robot head",
                    "Decorative vase",
                  ].map((q) => (
                    <button
                      key={q}
                      onClick={() => setPrompt(q)}
                      style={{
                        background: "rgba(124,58,237,0.1)",
                        border: "1px solid rgba(124,58,237,0.2)",
                        borderRadius: 6,
                        padding: "0.25rem 0.625rem",
                        color: "#a78bfa",
                        fontSize: "0.7rem",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <label style={{ fontSize: "0.7rem", color: "rgba(160,160,200,0.5)", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: "0.5rem" }}>
                Upload de imagem
              </label>
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: "2px dashed rgba(124,58,237,0.3)",
                  borderRadius: 12,
                  padding: "2rem 1rem",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  background: imagePreview ? "transparent" : "rgba(124,58,237,0.04)",
                  position: "relative",
                  overflow: "hidden",
                  minHeight: 140,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {imagePreview ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={imagePreview}
                    alt="preview"
                    style={{ maxHeight: 160, maxWidth: "100%", borderRadius: 8, objectFit: "contain" }}
                  />
                ) : (
                  <div>
                    <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>🖼</div>
                    <div style={{ fontSize: "0.8rem", color: "rgba(160,160,200,0.6)" }}>
                      Arraste ou clique para selecionar
                    </div>
                    <div style={{ fontSize: "0.65rem", color: "rgba(160,160,200,0.3)", marginTop: "0.25rem" }}>
                      PNG, JPG, WEBP
                    </div>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: "none" }}
                id="studio-image-input"
              />
              {imageFile && (
                <div style={{ marginTop: "0.5rem", fontSize: "0.7rem", color: "rgba(160,160,200,0.5)" }}>
                  {imageFile.name}
                </div>
              )}
            </div>
          )}

          {/* Options */}
          <div style={{ marginTop: "1.25rem" }}>
            <div style={{ fontSize: "0.7rem", color: "rgba(160,160,200,0.5)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.75rem" }}>
              Configurações
            </div>
            <div
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 10,
                padding: "0.875rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              {[
                { label: "Versão do modelo", value: "Hi3D v2.1" },
                { label: "Resolução", value: "1536 Fast" },
                { label: "Formato", value: "GLB" },
                { label: "Textura", value: "All-in-One" },
              ].map((opt) => (
                <div key={opt.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "0.75rem", color: "rgba(160,160,200,0.6)" }}>{opt.label}</span>
                  <span
                    style={{
                      fontSize: "0.7rem",
                      background: "rgba(124,58,237,0.15)",
                      color: "#a78bfa",
                      padding: "0.15rem 0.5rem",
                      borderRadius: 4,
                      fontWeight: 600,
                    }}
                  >
                    {opt.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Status */}
        {statusLabel() && (
          <div
            style={{
              margin: "0 1.5rem",
              padding: "0.75rem 1rem",
              borderRadius: 10,
              background: job?.status === "error"
                ? "rgba(239,68,68,0.1)"
                : job?.status === "done"
                ? "rgba(52,211,153,0.1)"
                : "rgba(124,58,237,0.1)",
              border: `1px solid ${job?.status === "error" ? "rgba(239,68,68,0.2)" : job?.status === "done" ? "rgba(52,211,153,0.2)" : "rgba(124,58,237,0.2)"}`,
              fontSize: "0.75rem",
              color: job?.status === "error" ? "#fca5a5" : job?.status === "done" ? "#6ee7b7" : "#a78bfa",
              marginBottom: "0.75rem",
            }}
          >
            {statusLabel()}
          </div>
        )}

        {/* Generate Button */}
        <div style={{ padding: "0 1.5rem 1.5rem" }}>
          <button
            id="studio-generate-btn"
            onClick={handleGenerate}
            disabled={isGenerating}
            style={{
              width: "100%",
              padding: "0.875rem",
              borderRadius: 12,
              border: "none",
              cursor: isGenerating ? "not-allowed" : "pointer",
              background: isGenerating
                ? "rgba(124,58,237,0.3)"
                : "linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)",
              color: "white",
              fontWeight: 700,
              fontSize: "0.95rem",
              transition: "all 0.3s",
              boxShadow: isGenerating ? "none" : "0 4px 20px rgba(124,58,237,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              letterSpacing: "0.01em",
            }}
          >
            {isGenerating ? (
              <>
                <span
                  style={{
                    display: "inline-block",
                    width: 14,
                    height: 14,
                    border: "2px solid rgba(255,255,255,0.3)",
                    borderTop: "2px solid white",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                Gerando…
              </>
            ) : (
              <>✦ Gerar Modelo 3D</>
            )}
          </button>


          {job?.status === "done" && job.modelUrl && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.75rem" }}>

              {/* STL — botão principal de impressão */}
              <button
                id="studio-stl-btn"
                onClick={handleDownloadStl}
                disabled={stlLoading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  padding: "0.875rem",
                  borderRadius: 12,
                  border: "none",
                  cursor: stlLoading ? "not-allowed" : "pointer",
                  background: stlLoading
                    ? "rgba(124,58,237,0.3)"
                    : "linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)",
                  color: "white",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  boxShadow: stlLoading ? "none" : "0 4px 20px rgba(236,72,153,0.35)",
                  transition: "all 0.3s",
                }}
              >
                {stlLoading ? (
                  <>
                    <span style={{
                      display: "inline-block", width: 13, height: 13,
                      border: "2px solid rgba(255,255,255,0.3)",
                      borderTop: "2px solid white", borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }} />
                    Reparando malha…
                  </>
                ) : (
                  <>🖨️ Preparar para Impressão 3D (STL)</>
                )}
              </button>

              {/* Badge de qualidade da malha após conversão */}
              {stlInfo && (
                <div style={{
                  fontSize: "0.7rem",
                  color: stlInfo.watertight ? "#6ee7b7" : "#fcd34d",
                  display: "flex", alignItems: "center", gap: "0.4rem",
                  padding: "0.375rem 0.75rem",
                  background: stlInfo.watertight ? "rgba(52,211,153,0.08)" : "rgba(245,158,11,0.08)",
                  border: `1px solid ${stlInfo.watertight ? "rgba(52,211,153,0.2)" : "rgba(245,158,11,0.2)"}`,
                  borderRadius: 8,
                }}>
                  {stlInfo.watertight ? "✓ Watertight" : "⚠ Malha com lacunas"}
                  {" · "}{stlInfo.faces.toLocaleString()} faces
                </div>
              )}

              {/* GLB — secundário */}
              <a
                href={job.modelUrl}
                target="_blank"
                rel="noopener noreferrer"
                id="studio-download-btn"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  gap: "0.5rem", padding: "0.625rem", borderRadius: 10,
                  border: "1px solid rgba(52,211,153,0.2)",
                  background: "rgba(52,211,153,0.05)",
                  color: "rgba(110,231,183,0.7)",
                  fontWeight: 600, fontSize: "0.75rem",
                  textDecoration: "none", transition: "all 0.2s",
                }}
              >
                ↓ Download GLB (Blender / Three.js)
              </a>
            </div>
          )}

        </div>

        {/* Spinner keyframe */}
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </aside>

      {/* ── Right Panel — 3D Viewer ──────────────────────────────────── */}
      <main
        style={{
          flex: 1,
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Top bar */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            padding: "0.875rem 1.25rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "linear-gradient(to bottom, rgba(8,8,18,0.9) 0%, transparent 100%)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
            <div
              style={{
                fontSize: "0.7rem",
                color: "rgba(160,160,200,0.5)",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
              }}
            >
              Visualizador 3D
            </div>
            {job?.status === "processing" && (
              <span
                style={{
                  background: "rgba(245,158,11,0.15)",
                  border: "1px solid rgba(245,158,11,0.3)",
                  color: "#fcd34d",
                  fontSize: "0.65rem",
                  padding: "0.15rem 0.5rem",
                  borderRadius: 4,
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                }}
              >
                PROCESSANDO
              </span>
            )}
            {job?.status === "done" && (
              <span
                style={{
                  background: "rgba(52,211,153,0.15)",
                  border: "1px solid rgba(52,211,153,0.3)",
                  color: "#6ee7b7",
                  fontSize: "0.65rem",
                  padding: "0.15rem 0.5rem",
                  borderRadius: 4,
                  fontWeight: 600,
                }}
              >
                ✓ MODELO CARREGADO
              </span>
            )}
          </div>

          <div style={{ display: "flex", gap: "0.5rem" }}>
            {["Perspectiva", "Topo", "Frente"].map((v) => (
              <button
                key={v}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 6,
                  padding: "0.25rem 0.625rem",
                  color: "rgba(160,160,200,0.7)",
                  fontSize: "0.7rem",
                  cursor: "pointer",
                }}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Three.js Canvas */}
        <ThreeViewer
          modelUrl={job?.modelUrl ?? null}
          isLoading={isGenerating || job?.status === "processing"}
        />

        {/* Task ID badge */}
        {job?.task_id && (
          <div
            style={{
              position: "absolute",
              bottom: "1rem",
              right: "1rem",
              background: "rgba(0,0,0,0.6)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 8,
              padding: "0.375rem 0.75rem",
              fontSize: "0.65rem",
              color: "rgba(160,160,200,0.5)",
              fontFamily: "monospace",
            }}
          >
            task: {job.task_id}
          </div>
        )}
      </main>
    </div>
  );
}
